import { useCallback } from "react";
import { Box, Chip, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import type {
  ProcessedFormField,
  ResolvedOption,
} from "@/types/api/formSchema";
import { resolveSelectOptions } from "../utils/schemaUtils";

type Props = {
  field: ProcessedFormField & { resolvedOptions?: ResolvedOption[] };
  value: string[];
  onChange: (value: string[]) => void;
  onBlur: () => void;
  name: string;
};

export function CheckboxField({
  field: schemaField,
  value = [],
  onChange,
}: Props) {
  const options = schemaField.resolvedOptions ?? resolveSelectOptions(schemaField);

  const handleToggle = useCallback(
    (optName: string) => {
      if (value.includes(optName)) {
        onChange(value.filter((v) => v !== optName));
      } else {
        onChange([...value, optName]);
      }
    },
    [value, onChange],
  );

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ display: "block", color: "text.secondary", mb: 1 }}
      >
        Select one or more
      </Typography>
      <Box
        role="group"
        aria-label={schemaField.resolvedLabel}
        sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
      >
        {options.map((opt) => {
          const isSelected = value.includes(opt.name);
          const isOptDisabled =
            !!schemaField.disabled ||
            ("disabled" in opt && !!(opt as ResolvedOption).disabled);
          return (
            <Chip
              key={opt.name}
              role="checkbox"
              aria-checked={isSelected}
              disabled={isOptDisabled}
              icon={isSelected ? <CheckIcon fontSize="small" /> : undefined}
              label={opt.display_name}
              onClick={() => !isOptDisabled && handleToggle(opt.name)}
              color={isSelected ? "primary" : "default"}
              variant={isSelected ? "filled" : "outlined"}
              sx={{ cursor: isOptDisabled ? "not-allowed" : "pointer" }}
            />
          );
        })}
      </Box>
    </Box>
  );
}
